import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Building2, Briefcase, Store, Check, Zap, Mountain, Users, Wrench, Home, ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SEO } from '../../components/SEO';

// Images de la page Savoie
const IMAGES = {
  hero: "/images/seo/savoie-hero.jpg",
  section: "/images/seo/savoie-section.jpg",
};

// Composant placeholder pour les images
function ImagePlaceholder({ label, className = "" }) {
  return (
    <div className={`bg-slate-200 dark:bg-slate-700 rounded-2xl flex flex-col items-center justify-center ${className}`}>
      <ImageIcon className="h-12 w-12 text-slate-400 mb-2" />
      <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">{label}</span>
    </div>
  );
}

export default function SavoiePage() {
  return (
    <main data-testid="seo-page-savoie" className="pt-32">
      <SEO 
        title="Création de site web en Savoie (73) | Chambéry, Aix-les-Bains, Albertville, Modane"
        description="Webdesigner freelance en Savoie. Création de sites web pour PME, indépendants et commerces locaux à Chambéry, Aix-les-Bains, Albertville, Saint-Jean-de-Maurienne. Devis gratuit sous 48h."
        path="/creation-site-web-savoie"
      />

      {/* Hero Section */}
      <section className="container-main mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/images/seo/savoie.png" 
                alt="Savoie" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-sm uppercase tracking-widest font-bold text-primary">
                Département de la Savoie (73)
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
              Création de site web
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-blue-600 pb-2">
                en Savoie
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              Webdesigner freelance disponible en Savoie, j'accompagne les <strong>PME, indépendants et commerces locaux</strong> du département. De <strong>Chambéry</strong> à <strong>Albertville</strong>, je me déplace pour vous rencontrer.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white text-lg px-8 py-6">
                  Discuter de mon projet
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/tarifs">
                <Button variant="outline" className="text-lg px-8 py-6">
                  Voir les tarifs
                </Button>
              </Link>
            </div>
          </div>

          {/* Image Hero */}
          <div className="animate-fade-up animation-delay-200">
            {IMAGES.hero ? (
              <>
                <img 
                  src={IMAGES.hero}
                  alt="Création site web Savoie - Paysage alpin"
                  className="w-full h-auto rounded-2xl object-cover shadow-2xl"
                />
              </>
            ) : (
              <ImagePlaceholder label="IMAGE HERO (paysage, montagnes, lac...)" className="w-full h-80 lg:h-96" />
            )}
          </div>
        </div>
      </section>

      {/* Section: Présent en Savoie */}
      <section className="bg-muted/30 section-padding">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image secondaire */}
            <div className="order-2 lg:order-1">
              {IMAGES.section ? (
                <>
                  <img 
                    src={IMAGES.section}
                    alt="Savoie"
                    className="w-full h-auto rounded-2xl object-cover"
                  />
                  <p className="text-xs text-muted-foreground mt-2 text-right">Chambéry, Savoie</p>
                </>
              ) : (
                <ImagePlaceholder label="IMAGE SECTION (Chambéry, ville, activité...)" className="w-full h-72 lg:h-80" />
              )}
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-sky-100 dark:bg-sky-950/50 text-sky-700 dark:text-sky-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Mountain className="h-4 w-4" />
                Disponible en Savoie
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Webdesigner freelance<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-blue-600">
                  du lac du Bourget aux sommets alpins
                </span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                La Savoie, c'est <strong>440 000 habitants</strong>, un territoire entre lacs et montagnes, 
                et une économie dynamique portée par le tourisme, l'industrie et les services.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Basé dans l'Ain, je me déplace régulièrement à <strong>Chambéry</strong>, <strong>Aix-les-Bains</strong> et <strong>Albertville</strong>. 
                Création de site web sur-mesure pour votre activité en Savoie.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <MapPin className="h-8 w-8 text-sky-600 shrink-0" />
                  <div>
                    <div className="font-bold">Mobile</div>
                    <div className="text-sm text-muted-foreground">Je me déplace</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Users className="h-8 w-8 text-sky-600 shrink-0" />
                  <div>
                    <div className="font-bold">Direct</div>
                    <div className="text-sm text-muted-foreground">Un seul interlocuteur</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Zap className="h-8 w-8 text-sky-600 shrink-0" />
                  <div>
                    <div className="font-bold">Réactif</div>
                    <div className="text-sm text-muted-foreground">Réponse sous 48h</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Wrench className="h-8 w-8 text-sky-600 shrink-0" />
                  <div>
                    <div className="font-bold">Sur-mesure</div>
                    <div className="text-sm text-muted-foreground">Adapté à vos besoins</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pour qui */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Pour qui ?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web pour les professionnels<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-blue-600">
                de Savoie
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Indépendants, PME, commerces locaux à Chambéry, Aix-les-Bains, Albertville... Un site web adapté à votre activité.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card: Indépendants */}
            <Card className="group hover:border-sky-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Briefcase className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Indépendants</h3>
                <p className="text-muted-foreground mb-4">
                  Consultants, professions libérales, coachs... Un site personnel qui génère des contacts qualifiés.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Personal branding
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Prise de RDV en ligne
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Optimisé SEO local
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: PME */}
            <Card className="group hover:border-sky-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">PME</h3>
                <p className="text-muted-foreground mb-4">
                  Entreprises de services, industrie, BTP... Un site vitrine professionnel pour valoriser votre expertise.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Présentation métiers
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Références clients
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Formulaire de contact pro
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Commerces locaux */}
            <Card className="group hover:border-sky-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Store className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Commerces locaux</h3>
                <p className="text-muted-foreground mb-4">
                  Restaurants, boutiques, artisans... Une vitrine en ligne pour attirer la clientèle locale et touristique.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Fiche Google optimisée
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Photos & galerie
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Horaires & contact
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section: Villes couvertes */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web dans toute la Savoie (73)
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto">
              Chambéry, Aix-les-Bains, Albertville, Modane, Saint-Jean-de-Maurienne... Je me déplace pour vous rencontrer.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Chambéry', desc: 'Préfecture', Icon: Building2 },
              { name: 'Aix-les-Bains', desc: 'Lac du Bourget', Icon: MapPin },
              { name: 'Albertville', desc: 'Porte des Alpes', Icon: Mountain },
              { name: 'Modane', desc: 'Maurienne', Icon: Mountain },
              { name: 'Saint-Jean-de-Maurienne', desc: 'Vallée industrielle', Icon: Building2 },
            ].map((zone) => (
              <div key={zone.name} className="text-center p-6 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-sky-600 to-blue-600 flex items-center justify-center mb-3">
                  <zone.Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-bold text-white text-sm">{zone.name}</h3>
                <p className="text-xs text-slate-400 mt-1">{zone.desc}</p>
              </div>
            ))}
          </div>

          {/* Stats Savoie */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-sky-400">440 000</div>
              <div className="text-sm text-slate-400">habitants</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-sky-400">273</div>
              <div className="text-sm text-slate-400">communes</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-sky-400">1h30</div>
              <div className="text-sm text-slate-400">depuis l'Ain</div>
            </div>
            <div className="text-center p-6 bg-white/5 rounded-xl">
              <div className="text-3xl font-black text-sky-400">Forfait</div>
              <div className="text-sm text-slate-400">Devis sous 48h</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Votre site web en Savoie
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Création de site internet à Chambéry, Aix-les-Bains, Albertville ou ailleurs en Savoie. Devis gratuit sous 48h.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 text-white text-lg px-8 py-6 w-full sm:w-auto">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/processus">
                <Button variant="outline" className="text-lg px-8 py-6 w-full sm:w-auto">
                  Découvrir mon processus
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Savoie (73)</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>Devis sous 48h</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
